import React from 'react';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DraggableWidget, WidgetConfig } from './DraggableWidget';
import { WidgetInsertionZone } from './WidgetInsertionZone';
import { cn } from '@/lib/utils';

interface EditableGridProps {
  widgets: WidgetConfig[];
  onReorder: (widgets: WidgetConfig[]) => void;
  onConfigure?: (widget: WidgetConfig) => void;
  onRemove?: (widgetId: string) => void;
  onAddWidget?: (type: string, config: any, index?: number) => void;
  isEditMode: boolean;
  children: (widget: WidgetConfig, index: number) => React.ReactNode;
  className?: string;
  widgetWithOpenInsights?: string | null;
}

export const EditableGrid: React.FC<EditableGridProps> = ({
  widgets,
  onReorder,
  onConfigure,
  onRemove,
  onAddWidget,
  isEditMode,
  children,
  className,
  widgetWithOpenInsights
}) => {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const activeWidget = widgets.find(widget => widget.id === activeId);

  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'small':
        return 'col-span-1';
      case 'medium':
        return 'col-span-1 md:col-span-2';
      case 'large':
        return 'col-span-1 md:col-span-2 lg:col-span-3';
      case 'full':
        return 'col-span-full';
      default:
        return 'col-span-1';
    }
  };

  const getDragOverlaySizeClasses = (size: string) => {
    switch (size) {
      case 'small':
        return 'w-64 h-32';
      case 'medium':
        return 'w-96 h-64';
      case 'large':
        return 'w-128 h-80';
      case 'full':
        return 'w-full h-64';
      default:
        return 'w-64 h-32';
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = widgets.findIndex((widget) => widget.id === active.id);
      const newIndex = widgets.findIndex((widget) => widget.id === over.id);

      onReorder(arrayMove(widgets, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={widgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
        <div className="relative">
        <div className={cn(
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6',
          'grid-flow-row-dense auto-rows-min',
          className
        )}>
          {widgets.map((widget, index) => (
            <DraggableWidget
              key={widget.id}
              widget={widget}
              isEditMode={isEditMode}
              onConfigure={onConfigure}
              onRemove={onRemove}
              isDragging={activeId === widget.id}
              forceFullWidth={widgetWithOpenInsights === widget.id}
            >
              {children(widget, index)}
            </DraggableWidget>
          ))}
        </div>
        
        {/* Overlay insertion zones */}
        {isEditMode && (
          <div className="absolute inset-0 pointer-events-none">
            <div className={cn(
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6',
              'grid-flow-row-dense auto-rows-min h-full'
            )}>
              {/* Insertion zones between widgets */}
              {widgets.map((widget, index) => (
                <React.Fragment key={`zone-${widget.id}`}>
                  {index === 0 && (
                    <div className="relative col-span-full">
                      <WidgetInsertionZone 
                        onAddWidget={onAddWidget} 
                        index={0} 
                        isEditMode={isEditMode} 
                        className="absolute -top-3 left-0 right-0 h-6 pointer-events-auto"
                      />
                    </div>
                  )}
                  <div 
                    className={cn(
                      getSizeClasses(widget.size),
                      'relative'
                    )}
                  >
                    <WidgetInsertionZone 
                      onAddWidget={onAddWidget} 
                      index={index + 1} 
                      isEditMode={isEditMode} 
                      className="absolute -bottom-3 left-0 right-0 h-6 pointer-events-auto"
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        </div>
      </SortableContext>
      
      <DragOverlay
        adjustScale={false}
        style={{
          transformOrigin: '0 0',
        }}
      >
        {activeWidget ? (
          <div className={cn(
            'bg-white/95 backdrop-blur-sm border-2 border-primary/50 rounded-lg shadow-2xl',
            getDragOverlaySizeClasses(activeWidget.size),
            'transform-gpu'
          )}>
            {children(activeWidget, widgets.findIndex(w => w.id === activeWidget.id))}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};